import type { AdTile, AuthCodeResponse, FeedPost, NotificationItem, PostAspectRatio, PushPublicKeyResponse, SessionState, StoryItem, UserSummary } from "../../shared/types";

async function request<T>(path: string, init?: RequestInit): Promise<T> {
  const headers = init?.body instanceof FormData ? init.headers : {
    "content-type": "application/json",
    ...init?.headers
  };
  const response = await fetch(path, {
    ...init,
    headers
  });
  const body = await response.json<{ data: T; error?: { message: string } }>();
  if (!response.ok) throw new Error(body.error?.message || "Request failed.");
  return body.data;
}

export const api = {
  session: () => request<SessionState>("/api/session"),
  feed: (offset = 0) => request<FeedPost[]>(`/api/feed?limit=12&offset=${offset}`),
  ads: (placement = "feed") => request<AdTile[]>(`/api/ads?placement=${encodeURIComponent(placement)}`),
  saved: () => request<FeedPost[]>("/api/saved"),
  stories: () => request<StoryItem[]>("/api/stories"),
  explore: () => request<{ posts: FeedPost[]; featuredAccounts: UserSummary[]; trending: Array<{ post: FeedPost; score: number }>; ads: AdTile[] }>("/api/explore"),
  profile: (username: string) => request<UserSummary>(`/api/users/${encodeURIComponent(username.replace(/^@/, ""))}`),
  profilePosts: (username: string) => request<FeedPost[]>(`/api/users/${encodeURIComponent(username.replace(/^@/, ""))}/posts`),
  post: (id: string) => request<FeedPost>(`/api/posts/${encodeURIComponent(id)}`),
  notifications: () => request<NotificationItem[]>("/api/notifications"),
  search: (q: string) => request<{ users: UserSummary[]; hashtags: string[] }>(`/api/search?q=${encodeURIComponent(q)}`),
  like: (id: string, liked: boolean) => request<FeedPost>(`/api/posts/${id}/like`, { method: liked ? "DELETE" : "POST" }),
  save: (id: string, saved: boolean) => request<FeedPost>(`/api/posts/${id}/save`, { method: saved ? "DELETE" : "POST" }),
  follow: (id: string, following: boolean) => request<{ ok: true }>(`/api/users/${id}/follow`, { method: following ? "DELETE" : "POST" }),
  comment: (id: string, body: string) => request<{ id: string }>(`/api/posts/${id}/comments`, { method: "POST", body: JSON.stringify({ body }) }),
  createPost: (caption: string, location: string, aspectRatio: PostAspectRatio, files: File[] = [], commentsEnabled = true) => {
    if (!files.length) {
      return request<FeedPost>("/api/posts", { method: "POST", body: JSON.stringify({ caption, location, aspectRatio, postType: aspectRatio === "9:16" ? "video" : "text", commentsEnabled }) });
    }
    const body = new FormData();
    body.set("caption", caption);
    body.set("location", location);
    body.set("aspectRatio", aspectRatio);
    body.set("commentsEnabled", String(commentsEnabled));
    for (const file of files) body.append("media", file);
    return request<FeedPost>("/api/posts", { method: "POST", body });
  },
  switchIdentity: (username: string) => request<SessionState["user"]>("/api/dev/session", { method: "POST", body: JSON.stringify({ username }) }),
  requestCode: (email: string, username?: string, displayName?: string) => request<AuthCodeResponse>("/api/auth/request-code", { method: "POST", body: JSON.stringify({ email, username, displayName }) }),
  verifyCode: (email: string, code: string) => request<SessionState>("/api/auth/verify-code", { method: "POST", body: JSON.stringify({ email, code }) }),
  logout: () => request<{ ok: true }>("/api/auth/logout", { method: "POST" }),
  updateProfile: (profile: { displayName: string; bio: string; website: string; location: string }) => request<UserSummary>("/api/me/profile", { method: "PATCH", body: JSON.stringify(profile) }),
  pushPublicKey: () => request<PushPublicKeyResponse>("/api/push/public-key"),
  subscribePush: (subscription: PushSubscriptionJSON) => request<{ ok: true }>("/api/push/subscribe", { method: "POST", body: JSON.stringify(subscription) }),
  unsubscribePush: (endpoint: string) => request<{ ok: true }>("/api/push/unsubscribe", { method: "POST", body: JSON.stringify({ endpoint }) }),
  adminDashboard: () => request<{ metrics: Record<string, number>; recentPosts: FeedPost[]; ads: AdTile[] }>("/api/admin/dashboard"),
  adminAds: () => request<AdTile[]>("/api/admin/ads"),
  uploadAdMedia: (file: File) => {
    const body = new FormData();
    body.set("file", file);
    return request<{ key: string; url: string }>("/api/admin/media", { method: "POST", body });
  },
  createAd: (ad: Partial<AdTile>) => request<{ id: string }>("/api/admin/ads", { method: "POST", body: JSON.stringify(ad) }),
  updateAd: (id: string, ad: Partial<AdTile>) => request<{ ok: true }>(`/api/admin/ads/${encodeURIComponent(id)}`, { method: "PATCH", body: JSON.stringify(ad) }),
  archiveAd: (id: string) => request<{ ok: true }>(`/api/admin/ads/${encodeURIComponent(id)}`, { method: "DELETE" }),
  adminLogin: (secret: string) => request<{ ok: true }>("/api/admin/login", { method: "POST", body: JSON.stringify({ secret }) })
};
